import type {
  Asset2CampaignGqlView,
  Asset2Source,
  DisplayAsset2,
  TextAsset2,
  VideoAsset2,
} from '../../../apis/graphql';
import type { Asset2Display, Asset2Text, Asset2Video } from '../../../models';

export const mockDAAPIAssetID = '17250bda-8354-11eb-8dcd-0242ac130003';

const mockAsset2BaseFields = {
  md5: 'db0eb52b3938ea673134f44ba2ebffa7',
  s3Url: 'https://asset2-manager-api-dev.aor.staging.hulu.com/v1/assets/download/beef8429-5321-47bb-ac31-2cf0403446f3',
  createdAt: '2025-10-17T21:18:21',
  updatedAt: '2025-10-17T21:18:21',
  lastSeenAt: '2025-10-17T21:18:21Z',
  assetTagList: [
    {
      id: 'id',
      value: 'value',
      description: 'description',
      displayName: 'display-name',
    },
  ],
  countryCodes: ['US' as const],
  language: 'ENGLISH' as const,
  countryReviews: [
    {
      country: 'US',
      status: 'ACCEPTED',
      allowedPublishers: ['DISNEY_PLUS'],
      declines: [],
    },
  ],
};

const createMockPreIngestAssetSource = (filename: string): Asset2Source => ({
  filename,
  source: null,
  mediaUrl:
    'https://hulu-expassetmanage-dev-us-west-2-expassetmanager.s3.us-west-2.amazonaws.com/org/df79ce4d-8373-4045-9a41-4ed998b15b39/asset/b4be1ed6-35c1-487f-a6d9-37d66966dfed.png',
  adomain: null,
  hash: null,
  sourceAdId: null,
  sourceCreativeId: null,
  contentPartnerId: null,
  timing: 'PRE_INGEST',
  mediaKey: null,
  createdAt: '2025-10-17T21:18:21',
  updatedAt: '2025-10-17T21:18:21',
});

const mockDisplayAsset2Specific = {
  height: 100,
  kinko: {
    status: 'SUCCESS',
    key:
      'https://img.huluqa.com/user/v3/ads/beef8429-5321-47bb-ac31-2cf0403446f3?base_image_bucket_name=ad_lifecycle&base_image=b4be1ed6-35c1-487f-a6d9-37d66966dfed-1760735900649.png',
  },
  width: 100,
  assetSources: [createMockPreIngestAssetSource('b4be1ed6-35c1-487f-a6d9-37d66966dfed.png')],
  ripcut: {
    status: 'SUCCESS',
    key: '3B80845EBF805AEF693D9046C34CCE7744004FC1A3B144398E401D109990E55E',
  },
};

const mockVideoMetadataCore = {
  bitRate: 22230960,
  frameRate: 23.976,
  duration: 5008,
  format: 'mp4',
  width: 1920,
  height: 1080,
  fileSizeBytes: 17656977,
} as const;

const mockVideoAsset2SharedFields = {
  ...mockAsset2BaseFields,
  name: 'test-name video',
  filename: 'test-name',
  subClass: 'VideoAsset' as const,
  audioMetadata: {
    recordId: null,
    duration: null,
  },
  transcode: {
    status: 'COMPLETED' as const,
    playbackId: '36',
    mediaDeliveryId: '5d5eceb8-30fb-4174-81c4-177943aed188',
    id: 'test-transcode-id-1',
    message: 'Test message',
    name: 'Transcode name',
    failureMessage: null,
    details: null,
  },
  priority: 'NORMAL' as const,
  assetSources: [
    {
      timing: 'DYNAMIC',
      mediaUrl:
        'https://hulu-ads-us-east-1-stg2-selfservice-assets.s3.amazonaws.com/qa/temp/mocked_02_asset2_target_5_sec-202509031526-01.mp4',
      filename: 'mocked_02_asset2_target_5_sec-202509031526-01.mp4',
      createdAt: '2025-09-03T15:26:32',
      updatedAt: '2025-09-03T15:26:32',
      sourceAdId: 'a80c6c47-eb2e-41a3-87a7-25a77f85d5ad',
      mediaKey:
        'https://hulu-ads-us-east-1-stg2-selfservice-assets.s3.amazonaws.com/qa/temp/mocked_02_asset2_target_5_sec-202509031526-01.mp4',
      contentPartnerId: '0e264149-3a71-4d46-8998-435b32314985',
      sourceCreativeId: '8d16d16b-7d8c-41d2-b850-4b74ea048c28',
      adomain: 'nike.com',
      source: null,
      hash: null,
    },
  ],
};

export const getMockExtendedAsset2Display = (asset?: Partial<Asset2Display>): Asset2Display => {
  return {
    ...mockAsset2BaseFields,
    id: 'test-asset-display-1',
    name: 'test-name display',
    filename: 'test-name',
    subClass: 'DisplayAsset',
    ...mockDisplayAsset2Specific,
    review: 'ACCEPTED',
    rejectionReasons: undefined,
    ...asset,
  };
};

export const getMockExtendedAsset2Video = (asset?: Partial<Asset2Video>): Asset2Video => {
  return {
    ...mockVideoAsset2SharedFields,
    id: '"fab95a7f-d954-4f5a-a9b3-8098487e4686',
    videoMetadata: {
      ...mockVideoMetadataCore,
      recordId: 123,
    },
    review: 'ACCEPTED',
    rejectionReasons: undefined,
    ...asset,
  };
};

export const getMockExtendedAsset2Text = (asset?: Partial<Asset2Text>): Asset2Text => {
  return {
    ...mockAsset2BaseFields,
    id: 'test-asset-text-1',
    name: 'test-name text',
    filename: 'test-text-name',
    subClass: 'TextAsset',
    text: 'Text',
    assetSources: [],
    review: 'ACCEPTED',
    rejectionReasons: undefined,
    ...asset,
  };
};

export const getMockVideoAsset2GQL = (asset?: Partial<VideoAsset2>): VideoAsset2 => {
  return {
    __typename: 'VideoAsset2',
    ...mockVideoAsset2SharedFields,
    id: 'fab95a7f-d954-4f5a-a9b3-8098487e4686',
    videoMetadata: {
      ...mockVideoMetadataCore,
      recordId: null,
    },
    ...asset,
  };
};

export const getMockDisplayAsset2GQL = (asset?: Partial<DisplayAsset2>): DisplayAsset2 => {
  return {
    __typename: 'DisplayAsset2',
    ...mockAsset2BaseFields,
    id: 'test-asset-display-1',
    name: 'test-name display',
    filename: 'test-name',
    subClass: 'DisplayAsset',
    ...mockDisplayAsset2Specific,
    ...asset,
  };
};

export const getMockTextAsset2GQL = (asset?: Partial<TextAsset2>): TextAsset2 => {
  return {
    __typename: 'TextAsset2',
    ...mockAsset2BaseFields,
    id: 'text-asset-id',
    name: 'Text asset name',
    filename: 'Text asset name',
    subClass: 'TextAsset',
    assetSources: [createMockPreIngestAssetSource('Text asset name')],
    text: 'Test text',
    ...asset,
  };
};

export const getMockAsset2CampaignGQLView = (asset?: Partial<Asset2CampaignGqlView>): Asset2CampaignGqlView => {
  return {
    id: 'test-asset-campaign-gql-view-1',
    assetTagIds: ['asset-tag-id'],
    assetTagList: [
      {
        id: 'asset-tag-id',
        value: 'value',
        description: 'description',
        displayName: 'display-name',
      },
    ],
    countryReviews: [
      {
        country: 'US',
        status: 'ACCEPTED',
        allowedPublishers: ['HULU'],
        declines: [
          {
            reason: 'Test #1',
            internalComment: 'Test internal',
            externalComment: 'TEst external',
          },
        ],
      },
      {
        country: 'UK',
        status: 'ACCEPTED',
        allowedPublishers: ['HULU'],
        declines: [
          {
            reason: 'Test #2',
            internalComment: 'Test internal',
            externalComment: 'TEst external',
          },
        ],
      },
    ],
    createdAt: '2024-10-29T23:16:44Z',
    lastSeenAt: '2024-10-30T08:00:00Z',
    name: 'Asset name',
    subClass: 'VideoAsset',
    transcode: {
      details: ['Test details'],
      failureMessage: 'Test failure messages',
      id: 'test-transcode-id',
      mediaDeliveryId: 'test-media-delivery-id',
      message: 'Test message',
      name: 'Transcode name',
      playbackId: 'test-playback-id',
      status: 'FAILED' as const,
    },
    language: 'SPANISH',
    text: null,
    ...asset,
  };
};
